/* Pause / play control for autoplaying background video.
 *
 * Pairs with snippets/video-pause-toggle.liquid and section 11 of
 * custom.css. Loaded once from layout/theme.liquid, so one copy handles
 * every background video on the page.
 *
 * video-background.js injects the <video> from a <template> after page
 * load, so the element does not exist on first pass. Each wrapper gets a
 * MutationObserver that binds as soon as it appears, then disconnects.
 *
 * One copy per page. The click handler is delegated at document level, so
 * a second copy would add a second handler, and one click would play and
 * pause the video at once: the button would do nothing. The file stops
 * straight away if a copy has already run. bind() marks the toggle, so a
 * second scan is a no-op.
 *
 * The visitor's choice holds. video-background.js calls play() every time
 * the video comes on screen (its own IntersectionObserver), and once more
 * on the first tap anywhere if autoplay failed. Left to that, a pause
 * would last only until the video next scrolled into view, and under
 * reduced motion the video would start just after this file paused it.
 * So the toggle records what the visitor asked for on the wrapper
 * (data-video-choice), and any play they did not ask for is undone at
 * once: after they paused, or under reduced motion until they press play.
 */

(function () {
  /* Load guard: see the note at the top. */
  if (window.videoPauseLoaded) return;
  window.videoPauseLoaded = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setState(toggle, playing) {
    toggle.setAttribute('data-playing', playing ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay
    );
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-video-toggle]');
    if (!toggle) return;

    var wrap = toggle.closest('.video-background');
    var video = wrap && wrap.querySelector('video');
    if (!video) return;

    if (video.paused) {
      wrap.dataset.videoChoice = 'play';
      video.play();
    } else {
      wrap.dataset.videoChoice = 'pause';
      video.pause();
    }
  });

  function bind(wrap) {
    var toggle = wrap.querySelector('[data-video-toggle]');
    var video = wrap.querySelector('video');
    if (!toggle || !video || toggle.dataset.bound === 'true') return;
    toggle.dataset.bound = 'true';

    /* Drive the label off the video's own events rather than the click, so
       the control stays correct if anything else pauses or plays it. */
    video.addEventListener('play', function () { setState(toggle, true); });
    video.addEventListener('pause', function () { setState(toggle, false); });

    if (reduced.matches) {
      /* Someone has asked their OS not to animate things, so the video
         stays paused and the photo under it shows. */
      video.autoplay = false;
      video.pause();
    }

    /* Undo any play the visitor did not ask for (see the note at the top).
       Reduced motion is read at each play, so changing the setting while
       the page is open counts too. */
    video.addEventListener('play', function () {
      var choice = wrap.dataset.videoChoice;
      if (choice === 'pause' || (reduced.matches && choice !== 'play')) {
        video.pause();
      }
    });

    setState(toggle, !video.paused);
  }

  function scan() {
    document.querySelectorAll('.video-background').forEach(function (wrap) {
      if (wrap.querySelector('video')) {
        bind(wrap);
        return;
      }

      var observer = new MutationObserver(function () {
        if (wrap.querySelector('video')) {
          observer.disconnect();
          bind(wrap);
        }
      });

      observer.observe(wrap, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* Theme editor: re-scan when a section is re-rendered. */
  document.addEventListener('shopify:section:load', scan);
})();
